import { useEffect, useRef, useState } from "react";
import { ThumbsUp, ThumbsDown, Check } from "lucide-react";
import api from "../lib/api";

/**
 * HidroFeedbackBar — Wave 20 §16 unified feedback chip.
 *
 * Two modes, auto-detected from props:
 *
 * LEGACY (back-compat — existing `kind`/`targetId` callers keep working)
 *   <HidroFeedbackBar kind="brief" targetId={stop.id} />
 *   → POSTs to /api/hidro/feedback (old hidro_memory route).
 *
 * §16 (richer — for AI outputs that want avoid-pattern learning)
 *   <HidroFeedbackBar
 *      surface="meeting_debrief"
 *      surfaceRefId={meeting.id}
 *      aiOutput={refinedFromHidro}
 *      shippedGetter={() => currentSummaryValue}   // optional: edit-diff
 *      promptId={...} promptVersion={...} model={...}
 *   />
 *   → POSTs to /api/hidro/ai-feedback (new richer schema).
 *
 * The §16 mode also auto-captures the edit-diff between Hidro's
 * output and what the user finally shipped, on unmount or aiOutput
 * change. This is the gold-signal for §15 learning.
 */
export default function HidroFeedbackBar({
  // legacy
  kind,
  targetId,
  // §16
  surface,
  surfaceRefId,
  aiOutput,
  shippedGetter,
  promptId,
  promptVersion,
  model,
  // common
  compact = false,
  onRecorded,
  testId,
}) {
  const isV2 = !!surface;

  const [rating, setRating] = useState(null);
  const [note, setNote] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const feedbackIdRef = useRef(null);

  const tid = testId || (isV2
    ? `feedback-${surface}-${surfaceRefId || "default"}`
    : `feedback-${kind || "x"}-bar`);

  // Reset when the AI output / target changes
  useEffect(() => {
    setRating(null); setNote(""); setNoteOpen(false); setSent(false);
    feedbackIdRef.current = null;
  }, [aiOutput, surfaceRefId, targetId]);

  const post = async (vote, reasonText = "") => {
    try {
      if (isV2) {
        const { data } = await api.post("/hidro/ai-feedback", {
          surface,
          surface_ref_id: surfaceRefId || null,
          ai_output: String(aiOutput || "").slice(0, 60_000),
          thumbs: vote === "up" ? "up" : "down",
          reason: (reasonText || "").trim() || null,
          prompt_id: promptId || null,
          prompt_version: promptVersion || null,
          model: model || null,
        });
        feedbackIdRef.current = data?.id || null;
        onRecorded?.(data?.id);
      } else {
        await api.post("/hidro/feedback", {
          kind,
          target_id: targetId || null,
          rating: vote === "up" ? 5 : 1,
          note: reasonText || null,
        });
      }
      setSent(true);
    } catch (_e) { /* feedback is optional */ }
  };

  // §16 edit-diff capture on unmount / aiOutput change
  useEffect(() => {
    return () => {
      if (!isV2 || !aiOutput || !shippedGetter) return;
      try {
        const shipped = shippedGetter();
        if (!shipped || shipped === aiOutput) return;
        api.post("/hidro/ai-feedback/edit-diff", {
          feedback_id: feedbackIdRef.current,
          surface,
          surface_ref_id: surfaceRefId || null,
          ai_output: String(aiOutput).slice(0, 60_000),
          shipped: String(shipped).slice(0, 60_000),
        }).catch(() => {});
      } catch (_e) { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiOutput, surfaceRefId]);

  if (sent && !noteOpen) {
    return (
      <div
        className={`text-[10px] text-[#647087] italic inline-flex items-center gap-1 ${compact ? "" : ""}`}
        data-testid={`${tid}-done`}
      >
        <Check className="w-3 h-3" /> thanks — Hidro logged your feedback
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${compact ? "mt-1" : "mt-2"} print:hidden`} data-testid={tid}>
      <span className="text-[10px] uppercase tracking-wider text-[#647087]">Was this useful?</span>
      <button
        type="button"
        onClick={async () => { setRating(5); await post("up", ""); }}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] transition ${
          rating === 5
            ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"
            : "bg-white/5 text-[#a0aabf] border-white/10 hover:border-emerald-500/30 hover:text-emerald-300"
        }`}
        data-testid={`${tid}-up`}
      >
        <ThumbsUp className="w-3 h-3" /> yes
      </button>
      <button
        type="button"
        onClick={() => { setRating(1); setNoteOpen(true); }}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] transition ${
          rating === 1
            ? "bg-rose-500/10 text-rose-300 border-rose-500/40"
            : "bg-white/5 text-[#a0aabf] border-white/10 hover:border-rose-500/30 hover:text-rose-300"
        }`}
        data-testid={`${tid}-down`}
      >
        <ThumbsDown className="w-3 h-3" /> no
      </button>
      {noteOpen && (
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={isV2
              ? "What went wrong? (e.g. too long, wrong tone, made up a fact)"
              : "What would have been more helpful?"}
            className="hv-input text-[11px] py-1 flex-1 min-w-0"
            data-testid={`${tid}-note`}
            autoFocus
          />
          <button
            type="button"
            onClick={async () => { await post("down", note); setNoteOpen(false); }}
            className="hv-btn hv-btn-primary text-[11px] py-1 px-2"
            data-testid={`${tid}-submit`}
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
}
